import React, { useState, Fragment, useEffect } from "react";
import DataTable from "react-data-table-component";
import CountUp from "react-countup";
import { toast } from "react-toastify";
import API from "../../services/API";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import {
  Cell,
  LabelList,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
} from "recharts";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import "react-loading-skeleton/dist/skeleton.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-circular-progressbar/dist/styles.css";
import "./Homepage.css";

const Homepage = () => {
  const formColorMap = [
    "#0B4C9C",
    "#0F65C9",
    "#1E3A8A",
    "#0369A1",
    "#475569",
    "#64748B",
    "#C8A24A",
  ];

  const glowingIcon = L.divIcon({
    className: "",
    html: `<div class="glow-circle"></div>`,
    iconSize: [70, 70],
    iconAnchor: [0, 0],
  });

  const FitBounds = ({ clusters }) => {
    const map = useMap();

    useEffect(() => {
      if (clusters.length > 0) {
        const bounds = L.latLngBounds(
          clusters.map((c) => [c.latitude, c.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }, [clusters, map]);

    return null;
  };

  const getFormColor = (index) => {
    return formColorMap[index % formColorMap.length];
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [attendanceRate, setAttendanceRate] = useState({ attendance_rate: 0 });
  const [attendanceByForm, setAttendanceByForm] = useState([]);
  const [monthlyData, setMonthlyData] = useState({});
  const [trendRows, setTrendRows] = useState([]);
  const [dailyAbsences, setDailyAbsences] = useState([]);
  const [progressiveAbsences, setProgressiveAbsences] = useState([]);
  const [granularity] = useState("monthly");
  const [trendByForm, setTrendByForm] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState("all");

  const currentMonth = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [classMonthlyData, setClassMonthlyData] = useState({});
  const [loadingTrendByForm, setLoadingTrendByForm] = useState(true);

  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [locationClusters, setLocationClusters] = useState([]);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [selectedClusterDate, setSelectedClusterDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [classSummary, setClassSummary] = useState(null);
  const [loadingClassSummary, setLoadingClassSummary] = useState(false);

  const [fullScreen, setFullScreen] = useState(false);

  const [state, setState] = useState({
    totalStudent: 0,
  });

  const [unrecorded, setUnrecorded] = useState(0);
  const [claimParents, setClaimedParents] = useState(0);

  const [schoolMood, setSchoolMood] = useState(null);
  const [loadingMood, setLoadingMood] = useState(false);
  const [moodPeriod, setMoodPeriod] = useState("weekly");

  const [weeklyData, setWeeklyData] = useState({});
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const week = Math.ceil(
      ((now - new Date(year, 0, 1)) / 86400000 +
        new Date(year, 0, 1).getDay() +
        1) /
        7
    );
    return `${year}-W${String(week).padStart(2, "0")}`;
  });

  const [classViewMode, setClassViewMode] = useState("month");
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [lastMood, setLastMood] = useState(null);
  const [customMonthlyMatrix, setCustomMonthlyMatrix] = useState(null);
  const [loadingCustomMonthlyMatrix, setLoadingCustomMonthlyMatrix] =
    useState(false);
  const [showCustomHeatmap, setShowCustomHeatmap] = useState(true);
  const [customMatrixViewMode, setCustomMatrixViewMode] = useState("slot");

  const fetchCustomMonthlyClassMatrix = async (
    month,
    viewMode = customMatrixViewMode
  ) => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      token: localStorage.getItem("token"),
    };

    setLoadingCustomMonthlyMatrix(true);

    try {
      const res = await API.get("/customAttendance/events/monthlyClassMatrix", {
        headers,
        params: {
          month,
          view_mode: viewMode,
        },
      });

      if (res.data.response_code === 1000) {
        setCustomMonthlyMatrix(res.data.data || null);
      } else {
        toast.error(
          res.data.message || "Failed to load operational attendance matrix"
        );
        setCustomMonthlyMatrix(null);
      }
    } catch (err) {
      toast.error(err?.message || "Error fetching operational attendance matrix");
      setCustomMonthlyMatrix(null);
    } finally {
      setLoadingCustomMonthlyMatrix(false);
    }
  };

  useEffect(() => {
    fetchCustomMonthlyClassMatrix(selectedMonth, customMatrixViewMode);
  }, [selectedMonth, customMatrixViewMode]);

  useEffect(() => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      token: localStorage.getItem("token"),
    };

    setLoadingClusters(true);

    API.get("/analytics/attendance/locationClusters", {
      headers,
      params: { radius: 30, is_portal: true, date: selectedClusterDate },
    })
      .then((response) => {
        if (response.data.response_code === 1000) {
          setLocationClusters(response.data.data || []);
        } else {
          toast.error(response.data.message || "Failed to fetch location clusters");
          setLocationClusters([]);
        }
      })
      .catch((err) => {
        toast.error(err.message || "Location cluster API failed");
        setLocationClusters([]);
      })
      .finally(() => setLoadingClusters(false));
  }, [selectedClusterDate]);

  useEffect(() => {
    const fetchClassSummary = async () => {
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: localStorage.getItem("token"),
      };

      const selectedDate = `${selectedMonth}-01`;

      setLoadingClassSummary(true);

      try {
        const res = await API.post(
          "/attendance/attendanceSummaryByClassView",
          {
            is_portal: true,
            filter_type: classViewMode,
            date: selectedDate,
          },
          { headers }
        );

        if (res.data.response_code === 1000) {
          setClassSummary(res.data.data);
        } else {
          toast.error(res.data.message || "Failed to load department summary");
          setClassSummary(null);
        }
      } catch (err) {
        toast.error(err.message || "Error fetching department summary");
        setClassSummary(null);
      } finally {
        setLoadingClassSummary(false);
      }
    };

    fetchClassSummary();
  }, [selectedMonth, classViewMode]);

  useEffect(() => {
    let isMounted = true;

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      token: localStorage.getItem("token"),
    };

    setLoadingMood(true);

    const [y, m] = selectedMonth.split("-").map(Number);
    const lastMonthDate = new Date(y, m - 1, 1);
    lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
    const lastMonthStr = lastMonthDate.toISOString().slice(0, 7);

    const fetchCurrent = API.get("/attendance/attendanceMoodSummary", {
      headers,
      params: {
        is_portal: true,
        period: moodPeriod === "monthly" ? "month" : "week",
        month: selectedMonth,
      },
    });

    const fetchLast = API.get("/attendance/attendanceMoodSummary", {
      headers,
      params: { is_portal: true, period: "month", month: lastMonthStr },
    });

    Promise.all([fetchCurrent, fetchLast])
      .then(([currentRes, lastRes]) => {
        if (!isMounted) return;

        if (currentRes.data.response_code === 1000) {
          setSchoolMood(currentRes.data.data);
        } else {
          setSchoolMood(null);
        }

        if (lastRes.data.response_code === 1000) {
          setLastMood(lastRes.data.data);
        } else {
          setLastMood(null);
        }
      })
      .catch((err) => console.error("Sentiment API Error:", err))
      .finally(() => {
        if (isMounted) setLoadingMood(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedMonth, moodPeriod]);

  const [classes, setClass] = useState([]);
const [columns] = useState([
  {
    name: "Jabatan / Unit",
    sortable: true,
    center: true,
    grow: 2,
    cell: (row) => (
      <Link
        to={`/class-details/${row.class_id ?? row.id}`}
        style={{ color: "#0B4C9C", fontWeight: 700, textDecoration: "none" }}
      >
        {row.class_name}
      </Link>
    ),
  },
  {
    name: "Jumlah Kakitangan",
    selector: (row) => row.total_students,
    sortable: true,
    center: true,
  },
  {
    name: "Sudah Daftar Masuk",
    selector: (row) => row.total_checked_in,
    sortable: true,
    center: true,
  },
  {
    name: "Cuti Sakit",
    selector: (row) => row.total_mc,
    sortable: true,
    center: true,
  },
  {
    name: "Kadar Kehadiran (%)",
    selector: (row) => row.attendance_rate?.toFixed(2),
    sortable: true,
    center: true,
  },
]);

  const fetchClassMonthlyAttendance = async ({ classId, month } = {}) => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      token: localStorage.getItem("token"),
    };

    if (!classId || classId === "all" || !month) {
      setClassMonthlyData({});
      return;
    }

    try {
      setLoadingMonthly(true);

      const res = await API.get("/analytics/attendance/monthlyAverage", {
        headers,
        params: { is_portal: true, class_id: classId, month },
      });

      if (res.data.response_code === 1000) {
        setClassMonthlyData(res.data.data || {});
      } else {
        toast.error(res.data.message || "Failed to fetch monthly attendance", {
          autoClose: 5000,
        });
        setClassMonthlyData({});
      }
    } catch (err) {
      toast.error(err?.message || "Failed to fetch monthly attendance", {
        autoClose: 5000,
      });
      setClassMonthlyData({});
    } finally {
      setLoadingMonthly(false);
    }
  };

  useEffect(() => {
    if (classes.length > 0) {
      const defaultClassId = classes[0]?.class_id ?? classes[0]?.id;
      setSelectedClassId(defaultClassId);
      fetchClassMonthlyAttendance({
        classId: defaultClassId,
        month: selectedMonth,
      });
    }
  }, [classes]);

  useEffect(() => {
    fetchClassMonthlyAttendance({
      classId: selectedClassId,
      month: selectedMonth,
    });
  }, [selectedClassId, selectedMonth]);

  useEffect(() => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      token: localStorage.getItem("token"),
    };

    API.get("/portal/totalStudent", { headers }).then((response) => {
      if (response.data.response_code == 1000) {
        setState(response.data.data);
      } else {
        toast.error(`${response.data.message}`, { autoClose: 5000 });
      }
    });

    API.get("/portal/totalUnrecordedStudent", { headers }).then((response) => {
      if (response.data.response_code == 1000) {
        setUnrecorded(response.data.data.totalStudent);
      } else {
        toast.error(`${response.data.message}`, { autoClose: 5000 });
      }
    });

    API.get("/portal/totalParentsClaimed", { headers }).then((response) => {
      if (response.data.response_code == 1000) {
        setClaimedParents(response.data.data.totalStudent);
      } else {
        toast.error(`${response.data.message}`, { autoClose: 5000 });
      }
    });

    API.get("/portal/totalStudentClass", { headers }).then((response) => {
      if (response.data.response_code == 1000) {
        const fetchedClasses = response.data.data || [];
        setClass(fetchedClasses);

        if (fetchedClasses.length > 0) {
          setSelectedClassId(fetchedClasses[0].class_id ?? fetchedClasses[0].id);
        }
      } else {
        toast.error(`${response.data.message}`, { autoClose: 5000 });
      }
    });

    API.post("/portal/attendanceRateByForm", {}, { headers }).then(
      (response) => {
        if (response.data.code === 1000) {
          setAttendanceByForm(response.data.data);
        } else {
          toast.error(`${response.data.message}`, { autoClose: 5000 });
        }
      }
    );

    API.post("/portal/todayAttendanceRate", {}, { headers }).then((response) => {
      if (response.data.code === 1000) {
        setAttendanceRate(response.data.data);
      } else {
        toast.error(`${response.data.message}`, { autoClose: 5000 });
      }
    });

    API.get("/analytics/attendance/monthlyAverage", {
      headers,
      params: { is_portal: true, month: selectedMonth },
    }).then((response) => {
      if (response.data.response_code === 1000) {
        setMonthlyData(response.data.data || {});
      } else {
        toast.error(response.data.message || "Failed to fetch monthly trend", {
          autoClose: 5000,
        });
      }
    });

    API.get("/analytics/attendance/weeklyAverage", {
      headers,
      params: {
        is_portal: true,
        week: selectedWeek,
      },
    }).then((response) => {
      if (response.data.response_code === 1000) {
        setWeeklyData(response.data.data || {});
      } else {
        toast.error(response.data.message || "Failed to fetch weekly average", {
          autoClose: 5000,
        });
      }
    });

    API.get("/analytics/attendance/trend", {
      headers,
      params: { is_portal: true, month: selectedMonth },
    }).then((response) => {
      if (response.data.response_code === 1000) {
        setTrendRows(response.data.data.trendRows || []);
        setDailyAbsences(response.data.data.dailyAbsences || []);
        setProgressiveAbsences(response.data.data.progressiveAbsences || []);
      } else {
        toast.error(response.data.message || "Failed to fetch attendance trend", {
          autoClose: 5000,
        });
      }
    });

    API.get("/analytics/attendance/trendByForm", {
      headers,
      params: { is_portal: true, granularity },
    }).then((response) => {
      if (response.data.response_code === 1000) {
        setTrendByForm(response.data.data || {});
      } else {
        toast.error(
          response.data.message || "Failed to fetch department trend",
          { autoClose: 5000 }
        );
      }
    });
  }, [selectedMonth]);

  useEffect(() => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      token: localStorage.getItem("token"),
    };

    setLoadingTrendByForm(true);

    API.get("/analytics/attendance/trendByForm", {
      headers,
      params: {
        is_portal: true,
        granularity,
        month: selectedMonth,
      },
    })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const data = response.data.data;

          if (granularity === "monthly") {
            setTrendByForm(Array.isArray(data) ? data : []);
          } else {
            setTrendByForm(
              typeof data === "object" && !Array.isArray(data) ? data : {}
            );
          }
        } else {
          toast.error(
            response.data.message || "Failed to fetch department trend",
            {
              autoClose: 5000,
            }
          );
          setTrendByForm([]);
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to fetch department trend", {
          autoClose: 5000,
        });
        setTrendByForm([]);
      })
      .finally(() => setLoadingTrendByForm(false));
  }, [granularity, selectedMonth]);

  const totalPersonnelLocation = locationClusters.reduce(
    (s, c) => s + c.count,
    0
  );

  const getHeatmapColor = (percent) => {
    if (percent === null || percent === undefined) return "transparent";
    if (percent >= 90) return "#dbeafe";
    if (percent >= 80) return "#bfdbfe";
    if (percent >= 60) return "#fef3c7";
    if (percent >= 50) return "#fed7aa";
    return "#fecaca";
  };

  const getCustomMatrixCellColor = (percent) => {
    if (percent === null || percent === undefined) return "transparent";

    const safePercent = Number(percent || 0);

    if (safePercent >= 90) return "#dbeafe";
    if (safePercent >= 75) return "#bfdbfe";
    if (safePercent >= 50) return "#fef3c7";
    if (safePercent > 0) return "#fed7aa";

    return "#f8fafc";
  };

  return (
    <Fragment>
      <div className="mysp-dashboard">
        <div className="mysp-hero-card">
<div>
  <div className="mysp-kicker">PUSAT PENTADBIRAN SPGO.my</div>
  <h1>Papan Pemuka</h1>
  <p>
    Pantau kehadiran kakitangan kerajaan, aktiviti daftar masuk, prestasi
    jabatan, rekod lokasi, cuti sakit dan ringkasan bulanan dalam satu paparan
    yang mudah difahami.
  </p>
</div>

       <div className="mysp-hero-controls">
  <label>Bulan Laporan</label>
  <input
    type="month"
    className="form-control"
    value={selectedMonth}
    onChange={(e) => setSelectedMonth(e.target.value)}
  />
</div>
        </div>

<div className="row g-4">
  <div className="col-xl-3 col-md-6">
    <DashboardCard
      label="Kehadiran Hari Ini"
      tone="blue"
      value={
        loadingMonthly ? (
          <Skeleton width={120} height={36} />
        ) : (
          <>
            <CountUp
              end={attendanceRate.attendance_count || 0}
              separator=","
            />
            <span>
              (
              {Number(
                attendanceRate.attendance_percentage || 0
              ).toFixed(0)}
              %)
            </span>
          </>
        )
      }
      description="Jumlah kakitangan yang sudah daftar masuk hari ini"
    />
  </div>

  <div className="col-xl-3 col-md-6">
    <DashboardCard
      label="Cuti Sakit"
      tone="amber"
      value={
        <>
          <CountUp end={attendanceRate.total_mc || 0} separator="," />
          <span>
            ({Number(attendanceRate.mc_percentage || 0).toFixed(0)}%)
          </span>
        </>
      }
      description="Kakitangan yang direkodkan sebagai cuti sakit"
    />
  </div>

  <div className="col-xl-3 col-md-6">
    <DashboardCard
      label="Purata Bulanan"
      tone="green"
      value={
        <>
          <CountUp
            end={monthlyData.monthly_average_attendance_rate || 0}
            decimals={2}
          />
          <small>%</small>
        </>
      }
      description="Purata kadar kehadiran kakitangan untuk bulan ini"
    />
  </div>

  <div className="col-xl-3 col-md-6">
    <DashboardCard
      label="Kakitangan Berdaftar"
      tone="blue"
      value={<CountUp end={state.totalStudent || 0} separator="," />}
      description={
        <div className="mysp-card-tags">
          <span>Biometrik belum direkod: {unrecorded}</span>
          <span>
            Profil disahkan: {claimParents}{" "}
            (
            {state.totalStudent > 0
              ? ((claimParents / state.totalStudent) * 100).toFixed(1)
              : "0.0"}
            %)
          </span>
        </div>
      }
    />
  </div>
</div>
<div className="row g-4 mt-1">
  <div className="col-xl-4">
    <Panel
      title="Mood Kakitangan"
      subtitle="Gambaran umum berdasarkan rekod daftar masuk"
      actions={
        <div className="d-flex gap-2">
          <input
            type="month"
            className="form-control form-control-sm mysp-control"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
          <select
            className="form-select form-select-sm mysp-control"
            value={moodPeriod}
            onChange={(e) => setMoodPeriod(e.target.value)}
          >
            <option value="weekly">Mingguan</option>
            <option value="monthly">Bulanan</option>
          </select>
        </div>
      }
    >
    </Panel>
  </div>

  <div className="col-xl-8">
    <Panel
      title="Kehadiran Bulanan Mengikut Jabatan"
      subtitle="Purata kehadiran mengikut jabatan atau unit"
    >
      {loadingTrendByForm || trendByForm === null ? (
        <Skeleton height={360} borderRadius={20} />
      ) : Array.isArray(trendByForm) && trendByForm.length > 0 ? (
        <ResponsiveContainer
          width="100%"
          height={isMobile ? trendByForm.length * 58 : 360}
        >
          <BarChart
            data={trendByForm}
            layout={isMobile ? "vertical" : "horizontal"}
            margin={{
              top: 24,
              right: 24,
              left: isMobile ? 20 : 0,
              bottom: 10,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e5e7eb"
            />

            {isMobile ? (
              <>
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  tickFormatter={(v) => `${v}%`}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="form"
                  tick={{
                    fontSize: 12,
                    fill: "#0f172a",
                    fontWeight: 700,
                  }}
                  width={80}
                  axisLine={false}
                  tickLine={false}
                />
              </>
            ) : (
              <>
                <XAxis
                  dataKey="form"
                  tick={{
                    fontSize: 13,
                    fill: "#0f172a",
                    fontWeight: 700,
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  tickFormatter={(v) => `${v}%`}
                  axisLine={false}
                  tickLine={false}
                />
              </>
            )}

            <Tooltip
              formatter={(value) => [
                `${Number(value).toFixed(1)}%`,
                "Kehadiran Bulanan",
              ]}
              contentStyle={{
                border: "0",
                borderRadius: "16px",
                boxShadow: "0 18px 45px rgba(15,23,42,0.16)",
              }}
            />

            <Bar
              dataKey="monthly_attendance_rate"
              name="Kehadiran Bulanan"
              radius={isMobile ? [0, 12, 12, 0] : [14, 14, 0, 0]}
              barSize={isMobile ? 24 : 44}
            >
              {trendByForm.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={formColorMap[index % formColorMap.length]}
                />
              ))}
              <LabelList
                dataKey="monthly_attendance_rate"
                position={isMobile ? "right" : "top"}
                formatter={(v) => `${Number(v).toFixed(1)}%`}
                style={{
                  fill: "#0f172a",
                  fontWeight: 800,
                  fontSize: "12px",
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <EmptyState title="Tiada data kehadiran jabatan buat masa ini" />
      )}
    </Panel>
  </div>
</div>

{loadingClassSummary ? (
  <div className="mt-4">
    <Panel
      title="Matriks Kehadiran Jabatan"
      subtitle="Sedang memuatkan ringkasan kehadiran jabatan"
    >
      <Skeleton height={360} borderRadius={20} />
    </Panel>
  </div>
) : (
  classSummary &&
  (() => {
    const activeLabels = (
      classViewMode === "month"
        ? classSummary.days || []
        : classSummary.weeks || []
    )
      .map((label, index) => {
        const hasData = classSummary.classes.some((c) => {
          const val =
            classViewMode === "month" ? c.days[index] : c.weeks[index];
          const percent =
            typeof val === "number" ? val : val?.percent ?? 0;
          return percent > 0;
        });
        return { label, index, hasData };
      })
      .filter((item) => item.hasData);

    return (
      <div className={fullScreen ? "mysp-fullscreen" : "mt-4"}>
        <Panel
          title="Matriks Kehadiran Jabatan"
          subtitle="Kehadiran jabatan mengikut hari atau minggu yang aktif"
          actions={
            <div className="mysp-panel-actions">
              <input
                type="month"
                className="form-control form-control-sm mysp-control"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />

              <div className="mysp-segment">
                <button
                  className={classViewMode === "month" ? "active" : ""}
                  onClick={() => setClassViewMode("month")}
                >
                  Hari
                </button>
                <button
                  className={
                    classViewMode === "week_by_month" ? "active" : ""
                  }
                  onClick={() => setClassViewMode("week_by_month")}
                >
                  Minggu
                </button>
              </div>

              <button
                className={`mysp-action-btn ${
                  showHeatmap ? "danger" : ""
                }`}
                onClick={() => setShowHeatmap(!showHeatmap)}
              >
                {showHeatmap ? "Warna Aktif" : "Paparan Biasa"}
              </button>

              <button
                className="mysp-action-btn dark"
                onClick={() => {
                  setFullScreen(!fullScreen);
                  setZoom(1);
                }}
              >
                {fullScreen ? "Tutup" : "Besarkan"}
              </button>
            </div>
          }
        >
          <div className="mysp-table-toolbar">
            <div>
              <strong>{classSummary.classes?.length || 0}</strong>
              <span> kumpulan jabatan</span>
            </div>

            <div className="mysp-zoom">
              <button onClick={() => setZoom(Math.max(zoom - 0.1, 0.6))}>
                −
              </button>
              <span>{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom(Math.min(zoom + 0.1, 1.4))}>
                +
              </button>
            </div>
          </div>

          <div className="mysp-matrix-scroll">
            <table
              className="mysp-matrix-table"
              style={{ fontSize: `${zoom}rem` }}
            >
              <thead>
                <tr>
                  <th className="sticky-left">Jabatan / Unit</th>
                  {activeLabels.map(({ label }, i) => (
                    <th key={i}>{label}</th>
                  ))}
                  <th className="overall-head">Keseluruhan</th>
                </tr>
              </thead>

              <tbody>
                {classSummary.classes
                  .filter((c) => c.average_percent > 0)
                  .sort((a, b) =>
                    a.class_name.localeCompare(b.class_name, undefined, {
                      numeric: true,
                    })
                  )
                  .map((c, i) => (
                    <tr key={i}>
                      <td className="sticky-left class-cell">
                        <Link to={`/class-details/${c.class_id}`}>
                          {c.class_name}
                        </Link>
                      </td>

                      {activeLabels.map(({ index }) => {
                        const val =
                          classViewMode === "month"
                            ? c.days[index]
                            : c.weeks[index];
                        const percent =
                          typeof val === "number"
                            ? val
                            : val?.percent ?? 0;
                        const cellBg =
                          showHeatmap && percent > 0
                            ? getHeatmapColor(percent)
                            : "transparent";

                        return (
                          <td
                            key={index}
                            style={{ backgroundColor: cellBg }}
                          >
                            <strong>
                              {typeof val === "object"
                                ? `${val.attended}/${val.total_students}`
                                : `${percent.toFixed(0)}%`}
                            </strong>

                            {typeof val === "object" && (
                              <small>{percent.toFixed(0)}%</small>
                            )}
                          </td>
                        );
                      })}

                      <td
                        className="overall-cell"
                        style={{
                          backgroundColor: showHeatmap
                            ? getHeatmapColor(c.average_percent)
                            : "#f8fafc",
                        }}
                      >
                        {c.average_percent.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    );
  })()
)}

<div className="mt-4">
  <Panel
    title="Matriks Kehadiran Operasi"
    subtitle="Kehadiran khas mengikut jabatan dan tarikh"
    actions={
      <div className="mysp-panel-actions">
        <input
          type="month"
          className="form-control form-control-sm mysp-control"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        />

        <button
          className={`mysp-action-btn ${
            showCustomHeatmap ? "danger" : ""
          }`}
          onClick={() => setShowCustomHeatmap(!showCustomHeatmap)}
        >
          {showCustomHeatmap ? "Warna Aktif" : "Paparan Biasa"}
        </button>

        <div className="mysp-segment">
          <button
            className={customMatrixViewMode === "slot" ? "active" : ""}
            onClick={() => setCustomMatrixViewMode("slot")}
          >
            Acara
          </button>

          <button
            className={customMatrixViewMode === "date" ? "active" : ""}
            onClick={() => setCustomMatrixViewMode("date")}
          >
            Tarikh
          </button>
        </div>
      </div>
    }
  >
    {loadingCustomMonthlyMatrix ? (
      <Skeleton height={360} borderRadius={20} />
    ) : customMonthlyMatrix?.matrix?.length > 0 &&
      customMonthlyMatrix?.classes?.length > 0 ? (
      <>
        <div className="mysp-matrix-scroll">
          <table
            className="mysp-matrix-table"
            style={{ fontSize: `${zoom}rem` }}
          >
            <thead>
              <tr>
                <th className="sticky-left class-cell class-name-col">
                  Jabatan / Unit
                </th>

                {customMatrixViewMode === "slot"
                  ? (customMonthlyMatrix.slots || []).map((slot) => (
                      <th
                        key={
                          slot.typeId ||
                          slot.type_id ||
                          slot.eventName ||
                          slot.event_name
                        }
                      >
                        <div className="matrix-slot-head">
                          <strong>
                            {slot.eventName ||
                              slot.event_name ||
                              "Acara"}
                          </strong>
                          <span>
                            {slot.eventDate || slot.event_date || ""}
                          </span>
                        </div>
                      </th>
                    ))
                  : (customMonthlyMatrix.dates || []).map((date) => (
                      <th key={date}>
                        {new Date(date).toLocaleDateString("ms-MY", {
                          day: "2-digit",
                          month: "short",
                        })}
                      </th>
                    ))}

                <th className="overall-head">Jumlah</th>
              </tr>
            </thead>

            <tbody>
              {(customMonthlyMatrix.matrix || []).map((row) => {
                const className =
                  row.className || row.class_name || "Tidak Diketahui";
                const classId = row.classId || row.class_id || className;

                const cells =
                  customMatrixViewMode === "slot"
                    ? row.slots || []
                    : row.dates || [];

                const totalStudentsInClass =
                  row.totalStudentsInClass ??
                  row.total_students_in_class ??
                  0;

                const totalUnique =
                  row.totalUniqueStudents ??
                  row.total_unique_students ??
                  0;

                const possible =
                  customMatrixViewMode === "slot"
                    ? row.monthlyPossibleStudentSlotCount ??
                      row.monthly_possible_student_slot_count ??
                      totalStudentsInClass * cells.length
                    : row.monthlyPossibleStudentDateCount ??
                      row.monthly_possible_student_date_count ??
                      totalStudentsInClass * cells.length;

                const rowPercent =
                  row.attendancePercent ??
                  row.attendance_percent ??
                  (possible > 0
                    ? (Number(totalUnique || 0) /
                        Number(possible || 1)) *
                      100
                    : 0);

                return (
                  <tr key={classId}>
                    <td className="sticky-left class-cell class-name-col">
                      <strong>{className}</strong>
                    </td>

                    {cells.map((cell, index) => {
                      const uniqueStudents =
                        cell?.uniqueStudents ??
                        cell?.unique_students ??
                        0;

                      const totalStudents =
                        cell?.totalStudentsInClass ??
                        cell?.total_students_in_class ??
                        totalStudentsInClass;

                      const percent =
                        cell?.attendancePercent ??
                        cell?.attendance_percent ??
                        0;

                      const key =
                        customMatrixViewMode === "slot"
                          ? `${classId}-${
                              cell?.typeId || cell?.type_id || index
                            }`
                          : `${classId}-${cell?.date || index}`;

                      const cellBg =
                        showCustomHeatmap && Number(percent) > 0
                          ? getCustomMatrixCellColor(percent)
                          : "transparent";

                      return (
                        <td key={key} style={{ backgroundColor: cellBg }}>
                          <div className="matrix-cell-compact">
                            <strong>
                              {uniqueStudents}/{totalStudents}
                            </strong>
                            <span>
                              {Number(percent || 0).toFixed(0)}%
                            </span>
                          </div>
                        </td>
                      );
                    })}

                    <td
                      className="overall-cell"
                      style={{
                        backgroundColor: showCustomHeatmap
                          ? getCustomMatrixCellColor(rowPercent)
                          : "#f8fafc",
                      }}
                    >
                      <div className="matrix-cell-compact">
                        <strong>
                          {totalUnique}/{possible}
                        </strong>
                        <span>{Number(rowPercent || 0).toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    ) : (
      <EmptyState title="Tiada data kehadiran operasi untuk bulan ini" />
    )}
  </Panel>
</div>

{monthlyData?.daily_summary && (
  <div className="mt-4">
    <Panel
      title="Trend Kehadiran Harian Kakitangan"
      subtitle="Pergerakan kehadiran sepanjang hari bekerja aktif dalam bulan ini"
      actions={
        <input
          type="month"
          className="form-control form-control-sm mysp-control"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        />
      }
    >
      {loadingMonthly ? (
        <Skeleton height={320} borderRadius={20} />
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={monthlyData.daily_summary}
            margin={{ top: 16, right: 20, left: -16, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e5e7eb"
            />
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "#64748b" }}
              dy={10}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "#64748b" }}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip
              formatter={(value) => [
                `${Number(value).toFixed(2)}%`,
                "Kadar Kehadiran",
              ]}
              contentStyle={{
                border: "0",
                borderRadius: "16px",
                boxShadow: "0 18px 45px rgba(15,23,42,0.16)",
              }}
            />
            <Line
              type="monotone"
              dataKey="attendance_rate"
              stroke="#0B4C9C"
              strokeWidth={4}
              dot={{
                r: 3,
                fill: "#0B4C9C",
                strokeWidth: 2,
                stroke: "#fff",
              }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Panel>
  </div>
)}

{attendanceByForm.length > 0 && (
  <div className="mt-4">
    <Panel
      title="Pecahan Kehadiran Mengikut Jabatan"
      subtitle="Jumlah kakitangan hadir berbanding jumlah kakitangan berdaftar"
    >
      <div className="row g-4">
        {attendanceByForm.map((item, index) => (
          <div className="col-12 col-md-6 col-xl-4" key={index}>
            <div className="mysp-form-card">
              <div className="mysp-form-head">
                <span
                  style={{ backgroundColor: getFormColor(index) }}
                />
                <strong>{item.form}</strong>
              </div>

              <div className="mysp-form-body">
                <div className="mysp-progress-ring">
                  <CircularProgressbar
                    value={item.attendance_percentage}
                    text={`${item.attendance_percentage.toFixed(1)}%`}
                    styles={buildStyles({
                      textColor: "#0f172a",
                      pathColor: getFormColor(index),
                      trailColor: "#e5e7eb",
                      textSize: "18px",
                      strokeLinecap: "round",
                    })}
                  />
                </div>

                <div>
                  <h4>
                    {item.total_present} / {item.total_students}
                  </h4>
                  <p>Kakitangan hadir</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  </div>
)}

<div className="mt-4">
  <Panel
    title="Lokasi Daftar Masuk"
    subtitle="Kawasan atau pintu masuk yang mempunyai rekod daftar masuk kakitangan"
    actions={
      <div className="d-flex align-items-center gap-2">
        <span className="mysp-total-pill">
          Jumlah: {totalPersonnelLocation}
        </span>
        <input
          type="date"
          className="form-control form-control-sm mysp-control"
          value={selectedClusterDate}
          onChange={(e) => setSelectedClusterDate(e.target.value)}
        />
      </div>
    }
  >
    {loadingClusters ? (
      <Skeleton height={460} borderRadius={20} />
    ) : locationClusters.length === 0 ? (
      <EmptyState title="Tiada data lokasi daftar masuk buat masa ini" />
    ) : (
      <div className="mysp-map-wrap">
        <MapContainer
          center={[3.139, 101.6869]}
          zoom={16}
          scrollWheelZoom={true}
          style={{ height: "520px", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://www.carto.com/">CARTO</a>'
            subdomains={["a", "b", "c", "d"]}
          />

          {locationClusters.map((cluster, idx) => (
            <Marker
              key={idx}
              position={[cluster.latitude, cluster.longitude]}
              icon={glowingIcon}
            >
              <Popup>
                <b>Lokasi #{cluster.cluster_id}</b>
                <br />
                Kakitangan: {cluster.count}
                <br />
              </Popup>
            </Marker>
          ))}

          <FitBounds clusters={locationClusters} />
        </MapContainer>
      </div>
    )}
  </Panel>
</div>

<div className="mt-4">
  <Panel
    title="Pecahan Kehadiran Mengikut Jabatan"
    subtitle="Ringkasan kehadiran bagi setiap jabatan"
  >
    <DataTable
      columns={columns}
      data={classes}
      striped={false}
      center={true}
      persistTableHead
      customStyles={spgovDataTableStyles}
    />
  </Panel>
</div>

      </div>
    </Fragment>
  );
};

export default Homepage;

const DashboardCard = ({ label, value, description, tone = "blue" }) => {
  return (
    <div className={`mysp-stat-card ${tone}`}>
      <div className="mysp-stat-label">
        <span />
        {label}
      </div>

      <div className="mysp-stat-value">{value}</div>

      <div className="mysp-stat-description">{description}</div>
    </div>
  );
};

const Panel = ({ title, subtitle, actions, children }) => {
  return (
    <div className="mysp-panel">
      <div className="mysp-panel-header">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>

        {actions && <div className="mysp-panel-action-wrap">{actions}</div>}
      </div>

      <div className="mysp-panel-body">{children}</div>
    </div>
  );
};



const EmptyState = ({ title }) => {
  return (
    <div className="mysp-empty">
      <div />
      <strong>{title}</strong>
      <span>Tiada data yang sepadan dengan pilihan carian ini.</span>
    </div>
  );
};

const spgovDataTableStyles = {
  table: {
    style: {
      borderRadius: "20px",
      overflow: "hidden",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f5f9ff",
      borderBottomColor: "#dbeafe",
      minHeight: "56px",
    },
  },
  headCells: {
    style: {
      color: "#0B4C9C",
      fontSize: "12px",
      fontWeight: 900,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    },
  },
  rows: {
    style: {
      minHeight: "62px",
      borderBottomColor: "#eef2f7",
      fontSize: "14px",
      color: "#0f172a",
    },
    highlightOnHoverStyle: {
      backgroundColor: "#eff6ff",
    },
  },
};